import styled from 'styled-components';
import theme from '../../styles/theme';

type SpacingType =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11';

type ShadowType = '1' | '2' | '3';

type BorderRadiusType = '1' | '2' | '3' | '4' | '5';

type BorderWidthType = '1' | '2' | '3' | '4';

interface CardProps {
  spacing?:
  | SpacingType
  | {
    top?: SpacingType;
    left?: SpacingType;
    right?: SpacingType;
    bottom?: SpacingType;
  };
  boxShadow?: boolean | ShadowType;
  border?: boolean;
  borderWidth?: BorderWidthType;
  disabled?: boolean;
  pressed?: boolean;
  borderRadius?:
  | boolean
  | {
    topLeft?: boolean;
    topRight?: boolean;
    bottomLeft?: boolean;
    bottomRight?: boolean;
  };
  borderRadiusAmount: BorderRadiusType;
  onClick?: () => void;
  variant:
  | 'none'
  | 'primary'
  | 'secondary'
  | 'neutral'
  | 'warning'
  | 'success'
  | 'empty'
  | 'danger';
  modifier: 'regular' | 'weak';
  children: any;
  hasCursorPointer: boolean;
  width: string;
  maxWidth: string;
  height: string;
  margin?:
  | SpacingType
  | {
    top?: SpacingType;
    left?: SpacingType;
    right?: SpacingType;
    bottom?: SpacingType;
  }
  | string;
  display: string;
  gap: string;
  justifyContent:
  | 'none'
  | 'center'
  | 'flex-start'
  | 'flex-end'
  | 'space-between';
  justifyItems: 'none' | 'center' | 'flex-start' | 'flex-end' | 'space-between';
  alignItems: 'inherit' | 'center' | 'right' | 'baseline';
  flexWrap: 'nowrap' | 'wrap';
  contain?: string;
  withElevation?: boolean;
}

const getSpacingCard = (spacing) =>
  typeof spacing === 'object'
    ? Object.keys(spacing)
      .map(
        (space) =>
          `padding-${space}: ${theme.spacing[`spacing-${spacing[space]}`]};`
      )
      .join(' ')
    : `padding: ${theme.spacing[`spacing-${spacing}`]};`;

const getMarginCard = (margin) =>
  typeof margin === 'object'
    ? Object.keys(margin)
      .map(
        (space) =>
          `margin-${space}: ${theme.spacing[`spacing-${margin[space]}`]};`
      )
      .join(' ')
    : `margin: ${margin}`;

const getBorderRadiusCard = (borderRadius, borderRadiusAmount) =>
  typeof borderRadius === 'object'
    ? `
    border-radius: 
    ${borderRadius.topLeft ? theme.borderRadius[`border-radius-${borderRadiusAmount}`] : '0'}
    ${borderRadius.topRight ? theme.borderRadius[`border-radius-${borderRadiusAmount}`] : '0'}
    ${borderRadius.bottomLeft ? theme.borderRadius[`border-radius-${borderRadiusAmount}`] : '0'}
    ${borderRadius.bottomRight ? theme.borderRadius[`border-radius-${borderRadiusAmount}`] : '0'};
`
    : borderRadius &&
    `border-radius: ${theme.borderRadius[`border-radius-${borderRadiusAmount}`]}; `;

const getVariantCard = (variant, modifier) =>
  variant === 'none'
    ? 'background: "none"'
    : `background: ${theme.colors[variant].bg[modifier]}; `;

const getBorderCard = (border, variant, modifier, borderWidth = '2') =>
  !border
    ? 'border: "none"'
    : `border: ${borderWidth}px solid ${theme.colors[variant].border[modifier === 'weak' && variant === 'primary' ? 'regular' : modifier]};`;

const getElevationCard = (boxShadow) =>
  typeof boxShadow === 'string'
    ? `box - shadow: ${theme.elevations[`elevation-${boxShadow}`]}; `
    : `box - shadow: none; `;

const Card = styled.div<CardProps>`
${({ withElevation }) => withElevation && `box-shadow: ${theme.elevations['elevation-1']};`}
  ${({ variant, modifier }) => variant && getVariantCard(variant, modifier)}
  ${({ border, variant, modifier, borderWidth }) =>
    border && getBorderCard(border, variant, modifier, borderWidth)}
  ${({ spacing }) => spacing && getSpacingCard(spacing)}
  ${({ margin }) => margin && getMarginCard(margin)};
  ${({ borderRadius, borderRadiusAmount }) =>
    borderRadius && getBorderRadiusCard(borderRadius, borderRadiusAmount)}
  ${({ boxShadow }) => boxShadow && getElevationCard(boxShadow)}
  ${({ disabled }) =>
    disabled &&
    `
    background: ${theme.colors.neutral.bg.regular};`}

  ${({ pressed }) =>
    pressed &&
    `
    background: ${theme.colors['secondary-1'].bg.weak};
  `}
   ${({ border, pressed }) =>
    border &&
    pressed &&
    `
    box-shadow:  inset 0 0 0 1px ${theme.colors['secondary-1'].border.weak};`}
  ${({ hasCursorPointer }) =>
    hasCursorPointer &&
    `
    cursor: pointer;
  `}
  ${({ width }) =>
    width &&
    `
    width: ${width};
  `}

  ${({ maxWidth }) =>
    maxWidth &&
    `
    max-width: ${maxWidth};
  `}
  ${({ height }) =>
    height &&
    `
    height: ${height};
  `}
  ${({ display }) =>
    display &&
    `
    display: ${display};
  `}
  ${({ gap }) =>
    gap &&
    `
    gap: ${gap};
  `}
  ${({ justifyContent }) =>
    justifyContent &&
    `
    justify-content: ${justifyContent};
  `}
  ${({ justifyItems }) =>
    justifyItems &&
    `
    justify-items: ${justifyItems};
  `}
  ${({ alignItems }) =>
    alignItems &&
    `
    align-items: ${alignItems};
  `}
  ${({ flexWrap }) =>
    flexWrap &&
    `
    flex-wrap: ${flexWrap};
  `}
  ${({ contain }) =>
    contain &&
    `
    contain: ${contain};
  `}
  ${({ flexDirection }: any) =>
    flexDirection &&
    `
    flex-direction: ${flexDirection};
  `}
`;

export { Card };
